import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Status } from "lib/enums";
import { fetchSiteDesign } from "../siteDesign/siteDesignActions";
import { CameraRecordingDataRow, SiteDesignGetPayload } from "lib/types";
import { RootState } from "reducers/store";

export type SiteDesignEditorState = {
    data: {
        [projectId: number]: {
            [siteId: number]: {
                [siteDesignId: number]: {
                    projectId: SiteDesignGetPayload['projectId'];
                    siteId: SiteDesignGetPayload['siteId'];
                    siteDesignId: SiteDesignGetPayload['siteDesignId'];
                    siteDesign: SiteDesignGetPayload['siteDesign'],
                    formData: SiteDesignGetPayload['formData']
                }
            }
        }
    },
    statuses: { [projectId: number]: { [siteId: number]: { [siteDesignId: number]: Status } } },
    upsertStatus: Status
};

const initialState: SiteDesignEditorState = {
    data: {},
    statuses: {},
    upsertStatus: Status.Idle
};

const sortCameras = (cameras: CameraRecordingDataRow[]): CameraRecordingDataRow[] => {

    return cameras
        .slice()
        .sort((a, b) => {
            // First, sort by cameraGroupId descending
            if (a.cameraGroupId !== b.cameraGroupId) {
                return b.cameraGroupId - a.cameraGroupId;
            }

            // If within the same group, prioritize the lowest streamId first
            const aIsLowestInGroup = a.streamId === Math.min(
                ...cameras
                    .filter(camera => camera.cameraGroupId === a.cameraGroupId)
                    .map(camera => camera.streamId)
            );
            const bIsLowestInGroup = b.streamId === Math.min(
                ...cameras
                    .filter(camera => camera.cameraGroupId === b.cameraGroupId)
                    .map(camera => camera.streamId)
            );

            if (aIsLowestInGroup) return -1;
            if (bIsLowestInGroup) return 1;

            // For the rest within the same group, sort by streamId descending
            return b.streamId - a.streamId;
        });
};

const siteDesignEditorSlice = createSlice({
    name: 'siteDesignEditor',
    initialState,
    reducers: {},
    extraReducers: builder => builder
        .addCase(fetchSiteDesign.pending, (state: SiteDesignEditorState, action) => {
            const { projectId, siteId, siteDesignId } = action.meta.arg || {};
            if (projectId !== undefined && siteId !== undefined && siteDesignId !== undefined) {
                state.statuses[projectId] = {
                    ...state.statuses[projectId],
                    [siteId]: {
                        ...state.statuses[projectId]?.[siteId],
                        [siteDesignId]: Status.Pending
                    }
                };
            }
        })
        .addCase(fetchSiteDesign.fulfilled, (state: SiteDesignEditorState, action: PayloadAction<SiteDesignGetPayload>) => {
            const payloadData = action.payload;

            if (payloadData) {
                const { projectId, siteId, siteDesignId, siteDesign, formData } = payloadData;

                if (projectId !== undefined && siteId !== undefined && siteDesignId !== undefined) {
                    // Check if siteDesign and cameras are defined
                    if (siteDesign && siteDesign.cameras) {
                        // Extract and sort cameras
                        const cameras = siteDesign.cameras;

                        const sortedCameras = sortCameras(cameras);

                        // Update the state with sorted cameras
                        state.data = {
                            ...state.data,
                            [projectId]: {
                                ...state.data[projectId],
                                [siteId]: {
                                    ...state.data[projectId]?.[siteId],
                                    [siteDesignId]: {
                                        siteDesign: {
                                            ...siteDesign,
                                            cameras: sortedCameras
                                        },
                                        formData,
                                        projectId,
                                        siteId,
                                        siteDesignId
                                    }
                                }
                            }
                        };

                        state.statuses = {
                            ...state.statuses,
                            [projectId]: {
                                ...state.statuses[projectId],
                                [siteId]: {
                                    ...state.statuses[projectId]?.[siteId],
                                    [siteDesignId]: Status.Success
                                }
                            }
                        };
                    } else {
                        console.warn("No cameras found in siteDesign.");
                    }
                }
            }
        })
        .addCase(fetchSiteDesign.rejected, (state: SiteDesignEditorState, action) => {
            const { projectId, siteId, siteDesignId } = action.meta.arg || {};
            if (projectId !== undefined && siteId !== undefined && siteDesignId !== undefined) {
                state.statuses[projectId] = {
                    ...state.statuses[projectId],
                    [siteId]: {
                        ...state.statuses[projectId]?.[siteId],
                        [siteDesignId]: Status.Rejected
                    }
                };
            }
            console.log("Fetch site design rejected, error: ", action.error);
        })
});

export const selectIsSiteDesignEditorLoading = (state: RootState): boolean => {
    const flattenedStatuses = Object.values(state.siteDesignEditor.statuses)
        .flatMap(siteStatuses => {
            return Object.values(siteStatuses)
                .flatMap(siteDesignStatuses => {
                    return Object.values(siteDesignStatuses);
                });
        });
 
    const hasPendingStatus = flattenedStatuses.some(status => status === Status.Pending);
   return hasPendingStatus || state.siteDesignEditor.upsertStatus === Status.Pending;
};


export default siteDesignEditorSlice.reducer;
